import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import store from 'store';
import Button from 'kingpin/atoms/Button';
import Modal from '@atlaskit/modal-dialog';
import AccountPickerContext from 'contexts/AccountPickerContext';
import Row from 'components/Common/Row';
import Inputs from 'components/Inputs';
import Dropdown from 'components/Inputs/Dropdown';

import usePerformanceDatasetsOptions from './usePerformanceDatasetsOptions';
import getIdentifier from 'getIdentifier';
import { buildShowBoard } from 'navigation/appRoutes';
import ToastContext from 'contexts/ToastContext';
import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import FilterPlates from 'components/FilterPlates';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import getTimeStamp from 'getTimeStamp';
import FleetOpsStaffOnly from '../../../components/Common/FleetOpsStaffOnly';
import FormInput from '../../../kingpin/forms/FormInput';
import Icon from '../../../kingpin/atoms/Icon';
import Tooltip from '../../../components/Tooltip';
import LabelsPicker from '../../../components/Inputs/LabelsPicker';
import Form from '../../../kingpin/forms/Form';
import FormHeader from '../../../kingpin/forms/FormHeader';
import FormContent from '../../../kingpin/forms/FormContent';

export const TopBarDiv = styled.div`
  box-shadow: var(--shadow-inset);
  margin-bottom: 24px;
`;

export const LabelDiv = styled.div`
  margin-bottom: 4px;
`;

export const toPlates = (drillDowns: FixedFilter[]): FilterPlateType[] => {
  return drillDowns.map((d) => ({
    id: d.id,
    type: 'Fixed',
    fixedValue: d,
  }));
};

const PerformanceBoardForm = ({
  close,
  board,
}: {
  board?: PerformanceBoardTypes.Board;
  close: () => void;
}) => {
  const { getPerformanceConfig } = useContext(DatasetDefinitionsContext);
  const { showToast } = useContext(ToastContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [title, setTitle] = useState<string>('');
  const [plates, setPlates] = useState<FilterPlateType[]>(() =>
    board ? board.plates : [],
  );
  const [isAsset, setIsAsset] = useState<boolean>(
    board ? !!board.isAsset : false,
  );
  const [labelIds, setLabelIds] = useState<string[]>(
    board ? board.labelIds : [],
  );

  const { datasetOptions, selectedDataset } = usePerformanceDatasetsOptions();
  const currentUser = useContext(CurrentUserContext);

  const [dataTypes, setDataTypes] = useState<string[]>([]);
  useEffect(() => {
    if (!selectedDataset) {
      setDataTypes([]);
      return;
    }
    setDataTypes([selectedDataset.type]);
  }, [selectedDataset]);
  const navigate = useNavigate();

  const onTitleChanged = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setTitle(event.target.value);
  };

  const getColumnOrderAndDefaultSort = useCallback(
    (
      dataset: FleetOps.DatasetDefinition,
    ): {
      columnOrder: PerformanceBoardTypes.Column[];
      defaultSort: {
        direction: 'asc' | 'desc';
        field: string;
      };
      columnSizeStrategy: FleetOps.ColumnSizeStrategies.Strategy;
    } => {
      if (dataset.isPerformance) {
        const config = getPerformanceConfig(dataset.type);
        if (!config) {
          return {
            columnOrder: [],
            defaultSort: {
              field: '',
              direction: 'asc',
            },
            columnSizeStrategy: { type: 'fitGridWidth', columnLimits: [] },
          };
        }

        return {
          columnOrder: [
            {
              key: getIdentifier(),
              field: config.entity,
              visible: true,
              type: 'field',
            },
          ],
          defaultSort: {
            field: config.entity,
            direction: 'asc',
          },
          columnSizeStrategy: {
            type: 'fitCellContents',
            skipHeader: false,
            pinnedColumns: [{ field: config.entity, width: 100 }],
          },
        };
      }

      return {
        columnOrder: [],
        defaultSort: {
          field: '',
          direction: 'asc',
        },
        columnSizeStrategy: { type: 'fitGridWidth', columnLimits: [] },
      };
    },
    [getPerformanceConfig],
  );

  const saveBoard = useCallback(() => {
    if (!selectedDataset) {
      alert('Please select a dataset');
      return;
    }

    const { columnOrder, defaultSort, columnSizeStrategy } =
      getColumnOrderAndDefaultSort(selectedDataset);

    const board: PerformanceBoardTypes.Board = {
      category: 'Performance' as 'Performance',
      id: getIdentifier(),
      name: title,
      dataType: selectedDataset.type,
      quickFilterOrder: [],
      columnOrder,
      slideout: [],
      plates,
      defaultSort,
      updatedBy: currentUser.id,
      updatedOn: getTimeStamp(),
      createdBy: currentUser.id,
      createdOn: getTimeStamp(),
      isEntityFilterEnabled: false,
      columnSizeStrategy,
      isAsset,
      labelIds,
      isV3Published: false,
      v3Access: {
        isPublic: false,
        userIds: [],
        groupIds: [],
      },
    };

    store.contentDistributions
      .getPerformanceBoardsRef({ accountId: selectedAccountId })
      .doc(board.id)
      .set(board)
      .then(() => {
        navigate(buildShowBoard(board.id));
        showToast('Board Created');
      });
  }, [
    currentUser.id,
    getColumnOrderAndDefaultSort,
    isAsset,
    labelIds,
    navigate,
    plates,
    selectedAccountId,
    selectedDataset,
    showToast,
    title,
  ]);

  const isDisabled = title === '' || !selectedDataset;

  return (
    <FilterPlatesProvider
      drillDowns={plates}
      setDrillDowns={setPlates}
      dataTypes={dataTypes}
    >
      <Modal onClose={close} shouldScrollInViewport={false} autoFocus={false}>
        <Form>
          <FormHeader title={'Create Board'} onClose={close} />
          <FormContent>
            <FormInput label={'Board Name'}>
              <Inputs.TextInput
                value={title}
                onChange={onTitleChanged}
                maxLength={40}
                autoFocus
                data-testid={'title'}
              />
            </FormInput>
            <FormInput label={'Select A Dataset'}>
              <Dropdown
                options={datasetOptions}
                placeholder={'Select Dataset'}
                isDisabled={datasetOptions.length === 0}
              />
            </FormInput>
            <FormInput label={'Filters'}>
              <FilterPlates />
            </FormInput>
            <FleetOpsStaffOnly>
              <FormInput label={'Is Asset'}>
                <Row centerAlign style={{ gap: 4 }}>
                  <Inputs.Checkbox isChecked={isAsset} onToggled={setIsAsset} />
                  <div style={{ cursor: 'help' }}>
                    <Tooltip
                      content={
                        "Boards which are an 'Asset' will show in the 'View all Content' and 'All Assets' screens in V3 to those users who have access to them"
                      }
                    >
                      <Icon icon={'help'} />
                    </Tooltip>
                  </div>
                </Row>
              </FormInput>
              <>
                {isAsset && (
                  <FormInput label={'Labels'}>
                    <LabelsPicker
                      labelIds={labelIds}
                      setLabelIds={setLabelIds}
                    />
                  </FormInput>
                )}
              </>
            </FleetOpsStaffOnly>
          </FormContent>
          <Row spaceBetween centerAlign>
            <Button
              onClick={close}
              type="Secondary"
              size="Small"
              label="Cancel"
            />
            <Button
              label="Create Board"
              testId="create-board-btn"
              onClick={saveBoard}
              isDisabled={isDisabled}
              type="Primary"
              size="Small"
            />
          </Row>
        </Form>
      </Modal>
    </FilterPlatesProvider>
  );
};

export default PerformanceBoardForm;
